import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BookOpen,
  CalendarClock,
  ChevronDown,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { getReportCategoryMetadata } from "@/lib/reports/report-categories";

export interface ToolbarReportItem {
  id: string;
  reportKey: string;
  name: string;
  category: string;
}

interface ReportsToolbarProps {
  reports: ToolbarReportItem[];
  /** Render prop for the saved-views content that goes inside the popover */
  savedViewsContent?: React.ReactNode;
  /** Render prop for the schedule content that goes inside the sheet */
  scheduleContent?: React.ReactNode;
  /** Action slot rendered on the right side of the toolbar (Share/Export/Run buttons) */
  actionSlot?: React.ReactNode;
  className?: string;
}

export function ReportsToolbar({
  reports,
  savedViewsContent,
  scheduleContent,
  actionSlot,
  className,
}: ReportsToolbarProps) {
  const { reportKey: activeReportKey } = useParams<{ reportKey: string }>();
  const navigate = useNavigate();
  const [reportsOpen, setReportsOpen] = useState(false);
  const [search, setSearch] = useState("");

  /** Close popover first, then navigate after the animation settles */
  const goToReport = useCallback(
    (path: string) => {
      setReportsOpen(false);
      // Small delay lets the Radix Popover close animation finish
      // before React Router triggers the route transition — prevents jitter.
      requestAnimationFrame(() => navigate(path));
    },
    [navigate],
  );

  const filteredReports = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => r.name.toLowerCase().includes(q));
  }, [reports, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, ToolbarReportItem[]>();
    filteredReports.forEach((r) => {
      const list = map.get(r.category) || [];
      list.push(r);
      map.set(r.category, list);
    });
    return Array.from(map.entries()).sort(([a], [b]) => {
      const aOrder = getReportCategoryMetadata(a).order;
      const bOrder = getReportCategoryMetadata(b).order;
      if (aOrder === bOrder) return a.localeCompare(b);
      return aOrder - bOrder;
    });
  }, [filteredReports]);

  const activeReportName = useMemo(() => {
    if (!activeReportKey) return "All Reports";
    const found = reports.find((r) => r.reportKey === activeReportKey);
    return found?.name ?? activeReportKey.replace(/-/g, " ");
  }, [activeReportKey, reports]);

  return (
    <div
      className={cn(
        "flex items-center gap-2 border-b border-border/60 bg-background/80 px-4 py-2 no-print",
        className,
      )}
    >
      {/* Reports dropdown */}
      <Popover
        open={reportsOpen}
        onOpenChange={(open) => {
          setReportsOpen(open);
          if (!open) setSearch("");
        }}
        modal={false}
      >
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            <BookOpen className="h-3.5 w-3.5" />
            <span className="max-w-[180px] truncate">{activeReportName}</span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align="start">
          <div className="border-b border-border/60 p-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search reports..."
                className="h-8 bg-background pl-8 text-sm"
              />
            </div>
          </div>
          <div className="max-h-[min(400px,60vh)] overflow-y-auto">
            <div className="p-2">
              <button
                type="button"
                onClick={() => goToReport("/reports")}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors",
                  !activeReportKey
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                All Reports
              </button>

              {grouped.map(([category, categoryReports]) => {
                const config = getReportCategoryMetadata(category);
                const Icon = config.icon;
                return (
                  <div key={category} className="mt-2">
                    <div className="flex items-center gap-2 px-3 py-1.5">
                      <Icon className={cn("h-3.5 w-3.5", config.color)} />
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {config.label}
                      </span>
                      <span className="text-xs text-muted-foreground/60">
                        {categoryReports.length}
                      </span>
                    </div>
                    {categoryReports.map((report) => {
                      const isActive = report.reportKey === activeReportKey;
                      return (
                        <button
                          key={report.id}
                          type="button"
                          onClick={() => goToReport(`/reports/${report.reportKey}`)}
                          className={cn(
                            "block w-full rounded-md px-3 py-1.5 pl-9 text-left text-[13px] transition-colors",
                            isActive
                              ? "bg-primary/10 font-medium text-primary"
                              : "text-muted-foreground hover:bg-accent hover:text-foreground",
                          )}
                        >
                          {report.name}
                        </button>
                      );
                    })}
                  </div>
                );
              })}

              {grouped.length === 0 && (
                <p className="px-3 py-4 text-center text-sm text-muted-foreground">
                  No reports match &ldquo;{search}&rdquo;
                </p>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Saved Views popover */}
      {savedViewsContent && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
              Saved Views
              <ChevronDown className="h-3 w-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[340px] p-4" align="start">
            {savedViewsContent}
          </PopoverContent>
        </Popover>
      )}

      {/* Schedule drawer */}
      {scheduleContent && (
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
              <CalendarClock className="h-3.5 w-3.5" />
              Schedule
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[400px] sm:w-[440px]">
            <SheetHeader>
              <SheetTitle>Schedule this report</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              {scheduleContent}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Action slot (Share/Export/Run buttons) - rendered on the right */}
      {actionSlot && (
        <div className="ml-auto flex items-center gap-2">
          {actionSlot}
        </div>
      )}
    </div>
  );
}
